import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Account } from "./account/Account";
import { BondsDashboardScreen } from "./bonds/BondsDashboardScreen";
import { Layout } from "./Layout";
import { NotFound } from "./NotFound";
import { ScrollToTop } from "@goodfill/react-scroll-to-top";
import { useRefreshUserDataHook } from "./useRefreshUserDataHook";
import { useRecoilState } from "recoil";
import { isGlobalStateLoadingState, userAuthState, userDataState } from "../state";
import { refreshToken } from "@goodfill/backend-api-client";
import { backendFetcher } from "./backendApi/backendApi";
import { route } from "../routes";
import { useRefreshBondDataHook } from "./useRefreshBondDataHook";
import { SignInScreen } from "./auth/SignIn/SignInScreen";
import { SignUpScreen } from "./auth/SignUp/SignUpScreen";
import { ForgotPasswordScreen } from "./auth/ForgotPassword/ForgotPasswordScreen";
import { BondDetailScreen as BondDetailScreen } from "./bonds/detail/BondDetailScreen";
import { CreateOrderScreen } from "./order/orderForm/createOrder/CreateOrderScreen";
import { ModifyOrderScreen } from "./order/orderForm/modifyOrder/ModifyOrderScreen";
import { OrderReceiptScreen } from "./order/orderForm/receipt/OrderReceiptScreen";
import { useLogout } from "./useLogout";

const REFRESH_TOKEN_INTERVAL_MS = 10 * 60 * 1000;
const BOND_LIST_REFRESH_POLLING_TIME = 3 * 1000;

function SecureRoute(props: { children: JSX.Element }) {
  const [, setIsGlobalStateLoading] = useRecoilState(isGlobalStateLoadingState);
  const [userAuthData, setUserAuthState] = useRecoilState(userAuthState);
  const [userData] = useRecoilState(userDataState);

  const navigate = useNavigate();

  const refreshUser = useRefreshUserDataHook();

  // rome-ignore lint/nursery/useExhaustiveDependencies: `userAuthData` required
  useEffect(() => {
    (async () => {
      await refreshUser();
    })();
  }, [
    userAuthData, // `userAuthData` required to refresh useEffect to get new data after login
    refreshUser,
  ]);

  const refreshBonds = useRefreshBondDataHook();

  useEffect(() => {
    (async () => {
      if (userData !== null) {
        await refreshBonds();
        setIsGlobalStateLoading(false); // Only after we get full data, we finish loading
      }
    })();
  }, [
    userData, // `userData` required to refresh useEffect to get new data after login
    refreshBonds,
    setIsGlobalStateLoading,
  ]);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (userData !== null) {
        await refreshBonds();
      }
    }, BOND_LIST_REFRESH_POLLING_TIME);

    return () => {
      clearTimeout(timer);
    };
  }, [userData, refreshBonds]);

  const handleError = () => {
    navigate(route.signIn());
  };

  const logout = useLogout();

  useEffect(() => {
    const timer = setInterval(async () => {
      if (userAuthData === null) {
        navigate(route.signIn());
        return;
      }

      (await refreshToken(backendFetcher, { refreshToken: userAuthData.refreshToken })).match({
        success: (data) => {
          setUserAuthState({ ...userAuthData, idToken: data.auth.AuthenticationResult.IdToken });
        },
        businessMatcher: { notAuthorized: handleError },
        unexpected: handleError,
        unauthorized: logout,
      });
    }, REFRESH_TOKEN_INTERVAL_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [userAuthData, handleError, navigate, setUserAuthState, logout]);

  return props.children;
}

function PublicRoute({ children }: { children: JSX.Element }) {
  return children;
}

export const Router = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route
          path="auth/*"
          element={
            <PublicRoute>
              <Layout />
            </PublicRoute>
          }
        >
          <Route path="sign-in" element={<SignInScreen />} />
          <Route path="forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="sign-up" element={<SignUpScreen />} />
        </Route>
        <Route
          path="/*"
          element={
            <SecureRoute>
              <Layout />
            </SecureRoute>
          }
        >
          <Route index={true} element={<BondsDashboardScreen />} />
          <Route path="bonds/:bondId" element={<BondDetailScreen />} />
          <Route path="bonds/:bondId/order/:orderDirection/:tradeTicketId" element={<CreateOrderScreen />} />
          <Route path="bonds/:bondId/order/:orderDirection" element={<CreateOrderScreen />} />
          <Route path="bonds/:bondId/modify-order/:orderId" element={<ModifyOrderScreen />} />
          <Route path="bonds/:bondId/receipt/:orderId" element={<OrderReceiptScreen />} />
          <Route path="account/*" element={<Account />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default Router;
